import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  FileStack,
  FileText,
  Filter,
  Info,
  Pencil,
  Plus,
  Power,
  Search,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import {
  BoutonMint,
  BoutonPrincipal,
  BoutonSecondaire,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { useToast } from '../components/ui/useToast';
import { ErreurApi } from '../lib/api';
import {
  useCreerReglePiece,
  useModifierReglePiece,
  useParametragePieces,
  useReordonnerReglesPieces,
  useSupprimerReglePiece,
  type CriterePiece,
  type EntreeCreationReglePiece,
  type EtatParametragePieces,
  type ModeDossier,
  type ReglePieceParametree,
} from '../lib/useParametragePieces';

const MODES: ModeDossier[] = ['CFA', 'OF'];

const LIBELLES_MODE: Record<ModeDossier, string> = {
  CFA: 'CFA — apprentissage',
  OF: 'OF — formation continue',
};

type FiltreMode = 'TOUS' | ModeDossier;
type FiltreEtat = 'TOUTES' | 'ACTIVES' | 'INACTIVES';

/** Brouillon initial vierge : modes standards sélectionnés, un fichier attendu, tous formats autorisés. */
export function brouillonNeuf(formats: string[]): EntreeCreationReglePiece {
  return {
    code: '',
    libelle: '',
    modes: [...MODES],
    obligatoire: true,
    critere: 'AUCUN',
    condition: null,
    nombreAttendu: 1,
    nombreMaximum: 1,
    formats,
    actif: true,
  };
}

export function versBrouillon(regle: ReglePieceParametree): EntreeCreationReglePiece {
  return {
    code: regle.code,
    libelle: regle.libelle,
    modes: regle.modes,
    obligatoire: regle.obligatoire,
    critere: regle.critere,
    condition: regle.condition,
    nombreAttendu: regle.nombreAttendu,
    nombreMaximum: regle.nombreMaximum,
    formats: regle.formats,
    actif: regle.actif,
  };
}

/**
 * Écran « Pièces justificatives » — pôle administratif et direction (CDC §4.3).
 *
 * Détermine les justificatifs exigés du candidat selon le mode de dossier (CFA/OF),
 * le nombre de fichiers attendus, les extensions autorisées et les critères de conditionnalité.
 */
export function AdmPiecesPage(): JSX.Element {
  const { data, isLoading, error } = useParametragePieces();
  const creer = useCreerReglePiece();
  const modifier = useModifierReglePiece();
  const supprimer = useSupprimerReglePiece();
  const reordonner = useReordonnerReglesPieces();
  const toast = useToast();

  const [edition, setEdition] = useState<{
    brouillon: EntreeCreationReglePiece;
    creation: boolean;
  } | null>(null);
  const [aSupprimer, setASupprimer] = useState<ReglePieceParametree | null>(null);

  const [recherche, setRecherche] = useState('');
  const [filtreMode, setFiltreMode] = useState<FiltreMode>('TOUS');
  const [filtreEtat, setFiltreEtat] = useState<FiltreEtat>('TOUTES');

  const tousFormats = useMemo(() => data?.formats.map((format) => format.code) ?? [], [data]);

  const filtree = useMemo(() => {
    const regles = data?.regles ?? [];
    const terme = recherche.trim().toLowerCase();
    return regles.filter((regle) => {
      if (filtreMode !== 'TOUS' && !regle.modes.includes(filtreMode)) return false;
      if (filtreEtat === 'ACTIVES' && !regle.actif) return false;
      if (filtreEtat === 'INACTIVES' && regle.actif) return false;
      if (terme.length === 0) return true;
      return (
        regle.libelle.toLowerCase().includes(terme) ||
        regle.code.toLowerCase().includes(terme) ||
        (regle.condition ?? '').toLowerCase().includes(terme)
      );
    });
  }, [data, recherche, filtreMode, filtreEtat]);

  if (error) {
    return (
      <div className="mx-auto max-w-[1360px] p-4 md:p-6">
        <MessageErreur
          message={
            error instanceof ErreurApi
              ? error.message
              : 'Le paramétrage des pièces n’a pas pu être chargé.'
          }
        />
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-[1360px] p-4 md:p-6">
        <Chargement libelle="Chargement du paramétrage des pièces…" />
      </div>
    );
  }

  const enEcriture =
    creer.isPending || modifier.isPending || supprimer.isPending || reordonner.isPending;

  const erreurEcriture = [creer.error, modifier.error, supprimer.error, reordonner.error].find(
    (candidate): candidate is ErreurApi => candidate instanceof ErreurApi,
  );

  const filtreActif =
    recherche.trim().length > 0 || filtreMode !== 'TOUS' || filtreEtat !== 'TOUTES';

  const deplacer = (code: string, sens: -1 | 1): void => {
    const index = data.regles.findIndex((regle) => regle.code === code);
    const cible = index + sens;
    if (index < 0 || cible < 0 || cible >= data.regles.length) return;
    const codes = data.regles.map((regle) => regle.code);
    [codes[index], codes[cible]] = [codes[cible]!, codes[index]!];
    reordonner.mutate(codes);
  };

  const enregistrer = (brouillon: EntreeCreationReglePiece, creation: boolean): void => {
    const mutation = creation ? creer : modifier;
    mutation.mutate(brouillon, {
      onSuccess: () => {
        setEdition(null);
        toast(
          creation
            ? 'Pièce ajoutée · elle est demandée dès maintenant aux dossiers ouverts'
            : 'Pièce enregistrée · les dossiers ouverts sont réalignés',
        );
      },
    });
  };

  const basculerActif = (regle: ReglePieceParametree): void => {
    modifier.mutate(
      { ...versBrouillon(regle), actif: !regle.actif },
      {
        onSuccess: () =>
          toast(
            regle.actif
              ? `« ${regle.libelle} » n’est plus demandée aux candidats`
              : `« ${regle.libelle} » est de nouveau demandée aux candidats`,
          ),
      },
    );
  };

  const actives = data.regles.filter((regle) => regle.actif);
  const inactives = data.regles.filter((regle) => !regle.actif);
  const parMode = (mode: ModeDossier): number =>
    actives.filter((regle) => regle.modes.includes(mode)).length;
  const conditionnelles = actives.filter((regle) => !regle.obligatoire).length;

  return (
    <div className="mx-auto max-w-[1360px] space-y-6 p-4 md:p-6">
      {/* En-tête de page standardisé Codialis */}
      <EnteteEcran
        titre="Pièces justificatives"
        sousTitre="Référentiel des justificatifs demandés aux candidats (CFA / OF), conditions d’exigibilité et formats · Pôle administratif et direction"
        actions={
          <BoutonMint
            onClick={() => setEdition({ brouillon: brouillonNeuf(tousFormats), creation: true })}
            disabled={enEcriture}
          >
            <span className="inline-flex items-center gap-2 font-medium">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Ajouter une pièce
            </span>
          </BoutonMint>
        }
      />

      {erreurEcriture && <MessageErreur message={erreurEcriture.message} />}

      {/* KPI / Indicateurs de synthèse */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <Compteur
          libelle="Demandées en CFA"
          valeur={parMode('CFA')}
          detail="Pièces actives du parcours apprentissage"
          ton="navy"
        />
        <Compteur
          libelle="Demandées en OF"
          valeur={parMode('OF')}
          detail="Pièces actives de la formation continue"
          ton="blue"
        />
        <Compteur
          libelle="Conditionnelles"
          valeur={conditionnelles}
          detail="Selon la situation réelle du candidat"
          ton="warm"
        />
        <Compteur
          libelle="Désactivées"
          valeur={inactives.length}
          detail="Plus demandées, dépôts passés conservés"
          ton="mint"
        />
      </div>

      {/* Barre de recherche et filtres */}
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-card">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <label className="relative min-w-0 flex-1" htmlFor="recherche-piece">
            <span className="sr-only">Rechercher une pièce justificative</span>
            <Search
              className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray"
              aria-hidden="true"
            />
            <input
              id="recherche-piece"
              type="search"
              value={recherche}
              onChange={(evenement) => setRecherche(evenement.target.value)}
              placeholder="Rechercher par libellé, code, condition…"
              className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-10 pr-3.5 text-sm text-codialis-navy placeholder:text-gray-400 focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20 transition"
            />
          </label>

          <div className="flex flex-wrap items-center gap-2">
            <Segments<FiltreMode>
              libelle="Filtrer par mode de dossier"
              valeur={filtreMode}
              onChange={setFiltreMode}
              options={[
                { valeur: 'TOUS', libelle: `Tous modes (${data.regles.length})` },
                { valeur: 'CFA', libelle: `CFA (${parMode('CFA')})` },
                { valeur: 'OF', libelle: `OF (${parMode('OF')})` },
              ]}
            />
            <Segments<FiltreEtat>
              libelle="Filtrer par état de la pièce"
              valeur={filtreEtat}
              onChange={setFiltreEtat}
              options={[
                { valeur: 'TOUTES', libelle: `Toutes (${data.regles.length})` },
                { valeur: 'ACTIVES', libelle: `Actives (${actives.length})` },
                { valeur: 'INACTIVES', libelle: `Désactivées (${inactives.length})` },
              ]}
            />
          </div>
        </div>

        {filtreActif && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-3 text-xs text-codialis-gray">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 font-medium text-codialis-navy">
                <Filter className="h-3.5 w-3.5 text-codialis-gray" aria-hidden="true" />
                {filtree.length} pièce{filtree.length > 1 ? 's' : ''} trouvée{filtree.length > 1 ? 's' : ''} sur {data.regles.length}
              </span>
              <span className="inline-flex items-center gap-1 text-codialis-gray">
                <Info className="h-3.5 w-3.5" aria-hidden="true" />
                L’ordre de présentation ne se modifie que sur la liste complète (sans filtre).
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setRecherche('');
                setFiltreMode('TOUS');
                setFiltreEtat('TOUTES');
              }}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-semibold text-codialis-sky transition hover:bg-gray-100"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              Effacer les filtres
            </button>
          </div>
        )}
      </div>

      {/* Tableau / Référentiel des pièces justificatives */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="font-display text-base font-bold text-codialis-navy">
              Référentiel des pièces demandées
            </h2>
            <p className="mt-0.5 text-xs text-codialis-gray">
              Dans l’ordre d’affichage officiel sur l’espace candidat et le CERFA
            </p>
          </div>
          <span className="rounded-lg bg-codialis-sand px-2.5 py-1 font-mono text-xs font-semibold text-codialis-navy">
            {filtree.length} / {data.regles.length}
          </span>
        </div>

        {filtree.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 sm:px-6 py-14 text-center">
            <FileStack className="h-9 w-9 text-gray-400" aria-hidden="true" />
            <p className="text-sm font-semibold text-codialis-navy">
              {filtreActif ? 'Aucune pièce ne correspond aux filtres' : 'Aucune pièce paramétrée'}
            </p>
            <p className="max-w-md text-xs text-codialis-gray">
              {filtreActif
                ? 'Modifiez ou effacez vos filtres pour retrouver l’ensemble du référentiel.'
                : 'Ajoutez une première pièce demandée au candidat : elle s’appliquera aux dossiers ouverts.'}
            </p>
            {filtreActif ? (
              <BoutonSecondaire
                onClick={() => {
                  setRecherche('');
                  setFiltreMode('TOUS');
                  setFiltreEtat('TOUTES');
                }}
              >
                Réinitialiser les filtres
              </BoutonSecondaire>
            ) : (
              <BoutonMint
                onClick={() => setEdition({ brouillon: brouillonNeuf(tousFormats), creation: true })}
                disabled={enEcriture}
              >
                Ajouter une pièce
              </BoutonMint>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtree.map((regle) => {
              const position = data.regles.findIndex((ligne) => ligne.code === regle.code);
              return (
                <LigneRegle
                  key={regle.code}
                  regle={regle}
                  position={position}
                  criteres={data.criteres}
                  premiere={position === 0}
                  derniere={position === data.regles.length - 1}
                  reordonnable={!filtreActif}
                  occupe={enEcriture}
                  onMonter={() => deplacer(regle.code, -1)}
                  onDescendre={() => deplacer(regle.code, 1)}
                  onBasculerActif={() => basculerActif(regle)}
                  onModifier={() => setEdition({ brouillon: versBrouillon(regle), creation: false })}
                  onSupprimer={() => setASupprimer(regle)}
                />
              );
            })}
          </ul>
        )}
      </div>

      {/* Note d'information réglementaire Codialis */}
      <div className="flex items-start gap-3 rounded-2xl bg-codialis-sand p-4 text-xs text-codialis-gray md:p-5">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-codialis-navy" aria-hidden="true" />
        <p className="leading-relaxed">
          <strong className="font-semibold text-codialis-navy">Règles administratives et pièces justificatives :</strong> Le code d’une pièce est figé dès sa création : il identifie les justificatifs déjà déposés sur les dossiers apprenants. Une pièce du socle réglementaire se désactive au lieu de se supprimer afin de garantir l’historique des CERFA et de l’espace candidat.
        </p>
      </div>

      {/* Fenêtre modale d'ajout / modification */}
      {edition && (
        <ModaleRegle
          etat={data}
          brouillon={edition.brouillon}
          creation={edition.creation}
          occupe={enEcriture}
          onChange={(brouillon) => setEdition({ ...edition, brouillon })}
          onFermer={() => setEdition(null)}
          onEnregistrer={() => enregistrer(edition.brouillon, edition.creation)}
        />
      )}

      {/* Fenêtre modale de confirmation de suppression */}
      <Modale
        titre="Supprimer cette pièce justificative ?"
        ouverte={aSupprimer !== null}
        onFermer={() => setASupprimer(null)}
        largeur="max-w-lg"
      >
        {aSupprimer && (
          <div className="space-y-4 px-4 sm:px-6 py-5">
            <p className="text-sm text-codialis-gray leading-relaxed">
              La pièce <strong className="font-semibold text-codialis-navy">{aSupprimer.libelle}</strong> ({aSupprimer.code}) ne sera plus demandée aux candidats, et les lignes ouvertes sans fichier déposé disparaîtront des {aSupprimer.dossiers} dossier(s) concerné(s).
            </p>
            {aSupprimer.fichiersDeposes > 0 && (
              <MessageErreur
                message={`${aSupprimer.fichiersDeposes} fichier(s) ont déjà été déposés pour cette pièce : la suppression est refusée afin d’éviter toute perte de justificatifs. Désactivez-la plutôt.`}
              />
            )}
            <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
              <BoutonSecondaire onClick={() => setASupprimer(null)}>Annuler</BoutonSecondaire>
              <BoutonPrincipal
                onClick={() =>
                  supprimer.mutate(aSupprimer.code, {
                    onSuccess: () => {
                      setASupprimer(null);
                      toast('Pièce supprimée du paramétrage');
                    },
                  })
                }
                disabled={enEcriture || aSupprimer.fichiersDeposes > 0}
              >
                Supprimer la pièce
              </BoutonPrincipal>
            </div>
          </div>
        )}
      </Modale>
    </div>
  );
}

/** Contrôle de filtre par segments exclusifs. */
function Segments<T extends string>({
  libelle,
  valeur,
  options,
  onChange,
}: {
  libelle: string;
  valeur: T;
  options: { valeur: T; libelle: string }[];
  onChange: (valeur: T) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label={libelle}
      className="inline-flex rounded-xl border border-gray-200 bg-white p-1"
    >
      {options.map((option) => (
        <button
          key={option.valeur}
          type="button"
          onClick={() => onChange(option.valeur)}
          aria-pressed={valeur === option.valeur}
          className={`min-h-[34px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg px-3 text-xs font-semibold transition ${
            valeur === option.valeur
              ? 'bg-codialis-navy text-white shadow-xs'
              : 'text-codialis-gray hover:bg-gray-50 hover:text-codialis-navy'
          }`}
        >
          {option.libelle}
        </button>
      ))}
    </div>
  );
}

function LigneRegle({
  regle,
  position,
  criteres,
  premiere,
  derniere,
  reordonnable,
  occupe,
  onMonter,
  onDescendre,
  onBasculerActif,
  onModifier,
  onSupprimer,
}: {
  regle: ReglePieceParametree;
  position: number;
  criteres: EtatParametragePieces['criteres'];
  premiere: boolean;
  derniere: boolean;
  reordonnable: boolean;
  occupe: boolean;
  onMonter: () => void;
  onDescendre: () => void;
  onBasculerActif: () => void;
  onModifier: () => void;
  onSupprimer: () => void;
}): JSX.Element {
  const critere = criteres.find((ligne) => ligne.code === regle.critere);

  return (
    <li
      className={`px-5 py-4 transition hover:bg-gray-50/70 ${
        !regle.actif ? 'bg-gray-50/40 opacity-80' : ''
      }`}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:gap-4">
        {/* Colonne Rang & Flèches de réordonnancement */}
        <div className="flex shrink-0 items-center gap-2 lg:flex-col lg:gap-1">
          <span
            title={`Ordre d’affichage : #${position + 1}`}
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-codialis-sand font-mono text-xs font-bold text-codialis-navy"
          >
            {position + 1}
          </span>
          {reordonnable && (
            <div className="flex gap-1 lg:flex-col">
              <button
                type="button"
                onClick={onMonter}
                disabled={premiere || occupe}
                title={premiere ? 'Déjà en première position' : 'Monter d’un rang'}
                aria-label={`Monter ${regle.libelle}`}
                className="flex h-6 w-6 items-center justify-center rounded-lg border border-gray-200 bg-white text-codialis-gray transition hover:border-gray-300 hover:text-codialis-navy disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ArrowUp className="h-3 w-3" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={onDescendre}
                disabled={derniere || occupe}
                title={derniere ? 'Déjà en dernière position' : 'Descendre d’un rang'}
                aria-label={`Descendre ${regle.libelle}`}
                className="flex h-6 w-6 items-center justify-center rounded-lg border border-gray-200 bg-white text-codialis-gray transition hover:border-gray-300 hover:text-codialis-navy disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ArrowDown className="h-3 w-3" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>

        {/* Détails de la pièce */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`text-sm font-semibold sm:text-base ${
                regle.actif ? 'text-codialis-navy' : 'text-codialis-gray line-through'
              }`}
            >
              {regle.libelle}
            </span>
            <span className="rounded-md bg-codialis-navy/10 px-2 py-0.5 font-mono text-xs font-bold text-codialis-navy">
              {regle.code}
            </span>
            {regle.socle && <Pastille texte="Socle réglementaire" ton="info" />}
            {regle.modes.map((mode) => (
              <Pastille key={mode} texte={mode} ton="mint" />
            ))}
            {regle.obligatoire ? (
              <Pastille texte="Obligatoire" ton="success" />
            ) : (
              <Pastille texte="Si concerné" ton="warning" />
            )}
            {!regle.actif && <Pastille texte="Désactivée" ton="neutral" />}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-codialis-gray">
            <span className="inline-flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5 text-codialis-gray" aria-hidden="true" />
              {regle.nombreAttendu > 1
                ? `${regle.nombreAttendu} fichiers attendus`
                : '1 fichier attendu'}
              {regle.nombreMaximum > regle.nombreAttendu
                ? ` (jusqu’à ${regle.nombreMaximum})`
                : ''}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-codialis-teal" aria-hidden="true" />
              {regle.libelleFormats}
            </span>
            {(regle.dossiers > 0 || regle.fichiersDeposes > 0) && (
              <span className="inline-flex items-center gap-1.5">
                <FileStack className="h-3.5 w-3.5 text-codialis-gray" aria-hidden="true" />
                {regle.dossiers} dossier(s) &bull; {regle.fichiersDeposes} fichier(s) déposé(s)
              </span>
            )}
          </div>

          {!regle.obligatoire && regle.critere !== 'AUCUN' && (
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 text-xs text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" />
              <span>
                <strong>Demandée si :</strong>{' '}
                {regle.condition ?? critere?.libelle ?? 'Situation particulière'}
              </span>
            </p>
          )}
        </div>

        {/* Boutons d'action rapides */}
        <div className="flex shrink-0 items-center gap-1.5 self-start pt-1">
          <button
            type="button"
            onClick={onBasculerActif}
            disabled={occupe}
            aria-label={
              regle.actif
                ? `Désactiver la pièce ${regle.libelle}`
                : `Réactiver la pièce ${regle.libelle}`
            }
            title={
              regle.actif
                ? 'Désactiver (ne plus demander cette pièce aux candidats)'
                : 'Réactiver cette pièce'
            }
            className={`flex h-8 w-8 items-center justify-center rounded-lg border transition disabled:opacity-40 ${
              regle.actif
                ? 'border-gray-200 bg-white text-codialis-gray hover:border-gray-300 hover:text-codialis-navy'
                : 'border-codialis-mint bg-codialis-mint/20 text-codialis-navy hover:bg-codialis-mint/30'
            }`}
          >
            <Power className="h-3.5 w-3.5" aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={onModifier}
            disabled={occupe}
            title="Modifier la pièce"
            aria-label={`Modifier la pièce ${regle.libelle}`}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-codialis-navy transition hover:border-gray-300 hover:bg-gray-50 disabled:opacity-40"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={onSupprimer}
            disabled={regle.socle || occupe || regle.fichiersDeposes > 0}
            title={
              regle.socle
                ? 'Pièce du socle réglementaire : non supprimable (désactivez-la plutôt).'
                : regle.fichiersDeposes > 0
                ? 'Des fichiers ont déjà été déposés : suppression refusée.'
                : 'Supprimer cette pièce'
            }
            aria-label={`Supprimer la pièce ${regle.libelle}`}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-red-600 transition hover:border-red-200 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </li>
  );
}

/** Fenêtre modale d'ajout ou d'édition d'une pièce justificative. */
function ModaleRegle({
  etat,
  brouillon,
  creation,
  occupe,
  onChange,
  onFermer,
  onEnregistrer,
}: {
  etat: EtatParametragePieces;
  brouillon: EntreeCreationReglePiece;
  creation: boolean;
  occupe: boolean;
  onChange: (brouillon: EntreeCreationReglePiece) => void;
  onFermer: () => void;
  onEnregistrer: () => void;
}): JSX.Element {
  const [codeTouche, setCodeTouche] = useState(!creation);

  useEffect(() => {
    if (!creation || codeTouche) return;
    onChange({ ...brouillon, code: codeDepuisLibelle(brouillon.libelle) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brouillon.libelle, codeTouche, creation]);

  const modifier = (champs: Partial<EntreeCreationReglePiece>): void =>
    onChange({ ...brouillon, ...champs });

  const basculerMode = (mode: ModeDossier): void =>
    modifier({
      modes: brouillon.modes.includes(mode)
        ? brouillon.modes.filter((candidat) => candidat !== mode)
        : [...brouillon.modes, mode],
    });

  const basculerFormat = (format: string): void =>
    modifier({
      formats: brouillon.formats.includes(format)
        ? brouillon.formats.filter((candidat) => candidat !== format)
        : [...brouillon.formats, format],
    });

  const codeValide = /^[A-Z][A-Z0-9_]{1,49}$/.test(brouillon.code);
  const libelleValide = brouillon.libelle.trim().length >= 3;
  const modesValides = brouillon.modes.length > 0;
  const formatsValides = brouillon.formats.length > 0;
  const nombresValides = brouillon.nombreMaximum >= brouillon.nombreAttendu;

  const manques = [
    !libelleValide && 'un libellé explicite (au moins 3 caractères)',
    creation && !codeValide && 'un code identifiant valide (majuscules, chiffres et « _ »)',
    !modesValides && 'au moins un mode de dossier concerné (CFA ou OF)',
    !formatsValides && 'au moins un format de fichier accepté',
    !nombresValides && 'un maximum de fichiers au moins égal au nombre attendu',
  ].filter((manque): manque is string => typeof manque === 'string');

  const enregistrable = !occupe && manques.length === 0;

  const critereChoisi = etat.criteres.find((critere) => critere.code === brouillon.critere);
  const apercu = [
    brouillon.libelle.trim() || 'Pièce sans libellé',
    brouillon.modes.length === 2 ? 'en CFA et en OF' : `en ${brouillon.modes[0] ?? '—'}`,
    brouillon.obligatoire
      ? 'à tous les candidats'
      : `si : ${brouillon.condition ?? critereChoisi?.libelle ?? 'situation particulière'}`,
    brouillon.nombreAttendu > 1 ? `${brouillon.nombreAttendu} fichiers` : '1 fichier',
  ].join(' · ');

  return (
    <Modale
      titre={creation ? 'Ajouter une pièce justificative' : `Modifier la pièce · ${brouillon.code}`}
      sousTitre={
        creation
          ? 'Définissez la pièce demandée aux candidats, ses modes de dossier et ses conditions.'
          : 'Les modifications réaligneront automatiquement les dossiers en cours.'
      }
      ouverte
      onFermer={onFermer}
      largeur="max-w-2xl"
    >
      <div className="space-y-6 px-4 sm:px-6 py-5">
        {/* Section 1 : Identité de la pièce */}
        <Section titre="Identité de la pièce">
          <Champ
            libelle="Libellé affiché au candidat"
            obligatoire
            aide="Intitulé clair et compréhensible par le candidat sur son espace personnel."
          >
            <input
              type="text"
              value={brouillon.libelle}
              onChange={(evenement) => modifier({ libelle: evenement.target.value })}
              placeholder="Ex. Attestation de scolarité"
              className={CHAMP_TEXTE}
            />
          </Champ>

          {creation && (
            <Champ
              libelle="Code identifiant"
              obligatoire
              aide="En majuscules avec « _ ». Généré automatiquement, figé après création."
            >
              <input
                type="text"
                value={brouillon.code}
                onChange={(evenement) => {
                  setCodeTouche(true);
                  modifier({ code: evenement.target.value.toUpperCase() });
                }}
                placeholder="Ex. ATTESTATION_SCOLARITE"
                aria-invalid={brouillon.code.length > 0 && !codeValide}
                className={`${CHAMP_TEXTE} font-mono uppercase ${
                  brouillon.code.length > 0 && !codeValide
                    ? 'border-red-300 focus:border-red-500'
                    : ''
                }`}
              />
            </Champ>
          )}
        </Section>

        {/* Section 2 : Périmètre & Conditions */}
        <Section titre="Périmètre & Exigibilité">
          <Champ libelle="Modes de dossier concernés" obligatoire commeBloc>
            <div className="flex flex-wrap gap-2 pt-0.5">
              {MODES.map((mode) => (
                <Bascule
                  key={mode}
                  actif={brouillon.modes.includes(mode)}
                  onClick={() => basculerMode(mode)}
                >
                  {LIBELLES_MODE[mode]}
                </Bascule>
              ))}
            </div>
          </Champ>

          <Champ libelle="À qui la pièce est-elle demandée ?" commeBloc>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 pt-0.5">
              <Choix
                actif={brouillon.obligatoire}
                titre="À tous les candidats"
                detail="Pièce obligatoire sur tous les dossiers du mode choisi."
                onClick={() => modifier({ obligatoire: true, critere: 'AUCUN' })}
              />
              <Choix
                actif={!brouillon.obligatoire}
                titre="Seulement si concerné"
                detail="Pièce conditionnelle, demandée selon la situation."
                onClick={() =>
                  modifier({
                    obligatoire: false,
                    critere: brouillon.critere === 'AUCUN' ? 'RQTH' : brouillon.critere,
                  })
                }
              />
            </div>
          </Champ>

          {!brouillon.obligatoire && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 pt-1">
              <Champ
                libelle="Situation qui la rend nécessaire"
                aide="Sélectionnez le critère technique de déclenchement."
              >
                <select
                  value={brouillon.critere}
                  onChange={(evenement) =>
                    modifier({ critere: evenement.target.value as CriterePiece })
                  }
                  className={CHAMP_TEXTE}
                >
                  {etat.criteres.map((critere) => (
                    <option key={critere.code} value={critere.code}>
                      {critere.libelle}
                    </option>
                  ))}
                </select>
              </Champ>

              <Champ
                libelle="Condition affichée au candidat"
                aide="Libellé explicatif pour l’apprenant (ex. « Hébergé par un tiers »)."
              >
                <input
                  type="text"
                  value={brouillon.condition ?? ''}
                  onChange={(evenement) =>
                    modifier({ condition: evenement.target.value.trim() || null })
                  }
                  placeholder="Ex. Hébergé par un tiers"
                  className={CHAMP_TEXTE}
                />
              </Champ>
            </div>
          )}
        </Section>

        {/* Section 3 : Fichiers attendus & Formats autorisés */}
        <Section titre="Fichiers & Formats autorisés">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Champ
              libelle="Nombre de fichiers attendus"
              obligatoire
              aide="Ex. 1 pour un diplôme, 2 pour des photos recto/verso."
            >
              <input
                type="number"
                min={1}
                max={10}
                value={brouillon.nombreAttendu}
                onChange={(evenement) => {
                  const nombreAttendu = Number(evenement.target.value);
                  modifier({
                    nombreAttendu,
                    nombreMaximum: Math.max(nombreAttendu, brouillon.nombreMaximum),
                  });
                }}
                className={CHAMP_TEXTE}
              />
            </Champ>

            <Champ
              libelle="Nombre maximum autorisé"
              obligatoire
              aide="Autorise des justificatifs en plusieurs feuillets."
            >
              <input
                type="number"
                min={brouillon.nombreAttendu}
                max={10}
                value={brouillon.nombreMaximum}
                onChange={(evenement) =>
                  modifier({ nombreMaximum: Number(evenement.target.value) })
                }
                className={CHAMP_TEXTE}
              />
            </Champ>
          </div>

          <Champ
            libelle="Formats autorisés"
            obligatoire
            aide="Le type MIME et l’extension du fichier déposé sont vérifiés à l’envoi."
            commeBloc
          >
            <div className="flex flex-wrap gap-2 pt-0.5">
              {etat.formats.map((format) => (
                <Bascule
                  key={format.code}
                  actif={brouillon.formats.includes(format.code)}
                  onClick={() => basculerFormat(format.code)}
                >
                  {format.libelle} ({format.extensions.join(', ')})
                </Bascule>
              ))}
            </div>
          </Champ>
        </Section>

        {/* Section 4 : Disponibilité */}
        <Section titre="Disponibilité">
          <Champ
            libelle="Statut de la pièce"
            aide="Désactivée, elle n’est plus demandée aux candidats ; les dépôts passés restent au dossier."
            commeBloc
          >
            <Bascule actif={brouillon.actif} onClick={() => modifier({ actif: !brouillon.actif })}>
              {brouillon.actif ? 'Pièce active (demandée aux candidats)' : 'Pièce désactivée'}
            </Bascule>
          </Champ>
        </Section>

        {/* Aperçu en temps réel */}
        <div className="flex items-start gap-3 rounded-xl bg-codialis-sand p-3.5 text-xs text-codialis-navy">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-codialis-gray" aria-hidden="true" />
          <div>
            <strong className="font-semibold">Aperçu de la règle : </strong>
            <span>{apercu}</span>
          </div>
        </div>

        {/* Liste des champs manquants si invalide */}
        {manques.length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <p className="font-semibold mb-1">Veuillez renseigner les informations obligatoires :</p>
            <ul className="list-disc list-inside space-y-0.5 text-amber-700">
              {manques.map((manque, idx) => (
                <li key={idx}>{manque}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-4">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonMint onClick={onEnregistrer} disabled={!enregistrable}>
            {creation ? 'Ajouter la pièce' : 'Enregistrer les modifications'}
          </BoutonMint>
        </div>
      </div>
    </Modale>
  );
}

const CHAMP_TEXTE =
  'w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm text-codialis-navy placeholder:text-gray-400 focus:border-codialis-sky focus:ring-2 focus:ring-codialis-sky/20 focus:outline-none transition min-h-[40px]';

/** Transformation d'un libellé vers un identifiant normalisé. Ex : « Attestation de scolarité » → ATTESTATION_DE_SCOLARITE */
function codeDepuisLibelle(libelle: string): string {
  return libelle
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);
}

function Section({ titre, children }: { titre: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="space-y-3.5">
      <h3 className="text-xs font-bold uppercase tracking-wider text-codialis-gray border-b border-gray-100 pb-1.5">
        {titre}
      </h3>
      {children}
    </section>
  );
}

function Choix({
  actif,
  titre,
  detail,
  onClick,
}: {
  actif: boolean;
  titre: string;
  detail: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={actif}
      className={`rounded-xl border p-3.5 text-left transition ${
        actif
          ? 'border-codialis-mint bg-codialis-mint/15 shadow-xs'
          : 'border-gray-200 bg-white hover:bg-gray-50'
      }`}
    >
      <span className="block text-xs font-bold text-codialis-navy">{titre}</span>
      <span className="mt-1 block text-xs text-codialis-gray leading-snug">{detail}</span>
    </button>
  );
}

function Bascule({
  actif,
  onClick,
  children,
}: {
  actif: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={actif}
      className={`min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] rounded-xl px-3.5 text-xs font-semibold transition border ${
        actif
          ? 'border-codialis-mint bg-codialis-mint text-codialis-navy font-bold'
          : 'border-gray-200 bg-white text-codialis-gray hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
}

function Champ({
  libelle,
  obligatoire = false,
  aide,
  children,
  commeBloc = false,
}: {
  libelle: string;
  obligatoire?: boolean;
  aide?: string;
  children: React.ReactNode;
  commeBloc?: boolean;
}): JSX.Element {
  const Enveloppe = commeBloc ? 'div' : 'label';
  return (
    <Enveloppe className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
        {obligatoire && (
          <span className="ml-1 text-red-500 font-bold" title="Champ obligatoire">
            *
          </span>
        )}
      </span>
      {children}
      {aide && <span className="mt-1 block text-[11px] text-codialis-gray">{aide}</span>}
    </Enveloppe>
  );
}
